import React from "react";

interface ConversationPreviewProps {
  startMessage?: string;
  autoResponderMessage?: string;
  destinationInfo?: {
    name: string;
    url: string;
  };
  aiChatEnabled?: boolean;
  aiContentSource?: {
    type: "DESTINATION_URL" | "UPLOADED_FILE";
    destinationId?: string;
    fileId?: string;
  };
  calendlyEnabled?: boolean;
}

export function ConversationPreview({
  startMessage,
  autoResponderMessage,
  destinationInfo,
  aiChatEnabled = false,
  aiContentSource,
  calendlyEnabled = false,
}: ConversationPreviewProps) {
  // SIMPLIFIED: Show empty state only if ALL props are truly empty
  const hasAnyContent =
    (startMessage && startMessage.trim().length > 0) ||
    (autoResponderMessage && autoResponderMessage.trim().length > 0) ||
    destinationInfo;

  if (!hasAnyContent) {
    return (
      <div
        className="bg-gray-50 rounded-lg p-6 text-center"
        data-testid="conversation-preview-empty"
      >
        <div className="w-12 h-12 bg-gray-200 rounded-lg mx-auto mb-3 flex items-center justify-center">
          <svg
            className="w-6 h-6 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
        <p className="text-sm text-gray-500">
          Select components to preview conversation flow
        </p>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4"
      data-testid="conversation-preview-content"
    >
      <h4 className="font-medium text-gray-900 mb-4 flex items-center">
        <svg
          className="w-5 h-5 text-blue-600 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
        Conversation Preview
      </h4>

      <div className="space-y-4">
        {/* Mobile Preview */}
        <div className="bg-gray-50 rounded-lg p-4">
          {/* <div className="flex items-center space-x-2 mb-3">
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">
              Mobile (SMS Flow)
            </span>
          </div> */}

          <div className="space-y-3">
            {/* Start Message */}
            {startMessage && (
              <div className="flex justify-end">
                <div className="bg-blue-500 text-white rounded-lg px-3 py-2 max-w-xs text-sm">
                  {startMessage}
                </div>
              </div>
            )}

            {/* Auto-responder Message */}
            {autoResponderMessage && (
              <div className="flex justify-start">
                <div className="bg-gray-200 text-gray-800 rounded-lg px-3 py-2 max-w-xs text-sm">
                  {autoResponderMessage}
                  {destinationInfo && (
                    <div className="mt-2 pt-2 border-t border-gray-300">
                      <a
                        href={destinationInfo.url}
                        className="text-blue-600 underline text-xs"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {destinationInfo.name}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Destination Info */}
            {destinationInfo && !autoResponderMessage && (
              <div className="flex justify-start">
                <div className="bg-gray-200 text-gray-800 rounded-lg px-3 py-2 max-w-xs text-sm">
                  <a
                    href={destinationInfo.url}
                    className="text-blue-600 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {destinationInfo.name}
                  </a>
                </div>
              </div>
            )}

            {/* AI Chat Messages */}
            {aiChatEnabled && (
              <>
                <div className="flex justify-start">
                  <div className="bg-blue-100 text-blue-800 rounded-lg px-3 py-2 max-w-xs text-sm">
                    <div className="flex items-center space-x-2 mb-1">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="font-medium">AI Assistant</span>
                    </div>
                    {aiContentSource?.type === "DESTINATION_URL"
                      ? "I see you're interested in our content. What questions can I answer for you?"
                      : aiContentSource?.type === "UPLOADED_FILE"
                      ? "I've analyzed the uploaded document. What would you like to know about it?"
                      : "Hello! I'm here to help answer your questions."}
                  </div>
                </div>

                {calendlyEnabled && (
                  <div className="flex justify-start">
                    <div className="bg-orange-100 text-orange-800 rounded-lg px-3 py-2 max-w-xs text-sm">
                      <div className="flex items-center space-x-2 mb-1">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V7a2 2 0 012-2h4a2 2 0 012 2v0M8 7v8a2 2 0 002 2h4a2 2 0 002-2V7M8 7H6a2 2 0 00-2 2v8a2 2 0 002 2h2m0 0h8m-8 0V9a2 2 0 012-2h4a2 2 0 012 2v8a2 2 0 01-2 2H10z"
                          />
                        </svg>
                        <span className="font-medium">Scheduling</span>
                      </div>
                      Would you like to schedule a follow-up appointment with
                      our team?
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* AI Chat Status */}
        {aiChatEnabled && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <div className="flex items-center space-x-2 mb-2">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span className="text-sm font-medium text-blue-900">
                AI Chat Enabled
              </span>
            </div>
            <div className="text-xs text-blue-700 space-y-1">
              <div className="flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Content Source:{" "}
                {aiContentSource?.type === "DESTINATION_URL"
                  ? "Destination URL"
                  : aiContentSource?.type === "UPLOADED_FILE"
                  ? "Uploaded File"
                  : "Not configured"}
              </div>
              {calendlyEnabled && (
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                  Appointment booking available
                </div>
              )}
            </div>
          </div>
        )}

        {/* Desktop Preview */}
        {/* <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-3">
            <svg
              className="w-4 h-4 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <span className="text-sm font-medium text-gray-700">
              Desktop (Chat Flow)
            </span>
          </div>

          <div className="space-y-3">
            {startMessage && (
              <div className="flex justify-end">
                <div className="bg-blue-500 text-white rounded-lg px-3 py-2 max-w-xs text-sm">
                  {startMessage}
                  <div className="text-xs text-blue-200 mt-1">
                    (auto-sent on page load)
                  </div>
                </div>
              </div>
            )}

            {autoResponderMessage && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 max-w-xs text-sm">
                  {autoResponderMessage}
                  {destinationInfo && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <a
                        href={destinationInfo.url}
                        className="text-blue-600 underline text-xs"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {destinationInfo.name}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {destinationInfo && !autoResponderMessage && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 max-w-xs text-sm">
                  <a
                    href={destinationInfo.url}
                    className="text-blue-600 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {destinationInfo.name}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div> */}

        {/* Flow Summary */}
        {/* {(startMessage || autoResponderMessage || destinationInfo) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <h5 className="text-sm font-medium text-blue-900 mb-2">
              Conversation Flow Summary
            </h5>
            <div className="text-xs text-blue-700 space-y-1">
              {startMessage && (
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  User sends: "{startMessage.substring(0, 30)}
                  {startMessage.length > 30 ? "..." : ""}"
                </div>
              )}
              {autoResponderMessage && (
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  System responds: "{autoResponderMessage.substring(0, 30)}
                  {autoResponderMessage.length > 30 ? "..." : ""}"
                </div>
              )}
              {destinationInfo && (
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                  User directed to: {destinationInfo.name}
                </div>
              )}
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
}
